import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Button } from "@gcs/design-system/react";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

import { ModalResponsive } from "../../../../components/common/Modal/Modal.Style";
import InputText from "../../../../components/Habitacional/core/Form/InputText";
import Select from "../../../../components/Habitacional/core/Form/Select";

import useWindowSize from "../../../../hooks/useWindowSize";

import * as S from "./ContactSectionForm.style";

const invalidEmail = "E-mail inválido";
const requiredMessage = "Campo obrigatório";

const contactSchema = yup.object().shape({
  name: yup.string().required(requiredMessage),
  email: yup.string().email(invalidEmail).required(requiredMessage),
  registrationNumber: yup.string().required(requiredMessage),
  contactPeriod: yup.string().required(requiredMessage),
  phone: yup.string().required(requiredMessage),
});

const ContactSectionForm = ({ isOpen, onToggle }) => {
  const contactFormRef = useRef(null);
  const [isOpenModal, setIsOpenModal] = useState(false);
  const { width } = useWindowSize();

  const isMobile = width && width <= 992;
  const isDisabledFields = !isOpen;

  const {
    control,
    reset,
    formState: { errors },
    handleSubmit,
  } = useForm({
    defaultValues: {
      name: "",
      email: "",
      registrationNumber: "",
      contactPeriod: "",
      phone: "",
    },
    resolver: yupResolver(contactSchema),
  });

  const scrollToMyRef = () => {
    contactFormRef.current.scrollIntoView(true);
    window.scrollBy({ top: -90, behavior: "smooth" });
  };

  const onSubmit = () => {
    setIsOpenModal(true);
    reset();
  };

  useEffect(() => {
    if (isOpen) {
      scrollToMyRef();
    }
  }, [isOpen]);

  return (
    <>
      <S.ContactSectionFormContainer $isOpen={isOpen} ref={contactFormRef}>
        <S.ContactSectionFormContent
          className="container"
          onSubmit={handleSubmit(onSubmit)}
        >
          <S.ContactSectionFormTitle>
            Informar dados para contato
          </S.ContactSectionFormTitle>

          <S.ContactSectionFormData>
            <Controller
              name="name"
              control={control}
              render={({ field }) => (
                <InputText
                  id="name"
                  name="name"
                  title="Nome"
                  placeholder="Nome completo"
                  maxLength={150}
                  value={field.value}
                  onChange={(e) => field.onChange(e.target.value)}
                  isRequired
                  hasError={errors?.name?.message}
                  errorMessage={errors?.name?.message}
                  {...field}
                />
              )}
            />
            <Controller
              name="email"
              control={control}
              render={({ field }) => (
                <InputText
                  id="email"
                  name="email"
                  title="E-mail"
                  placeholder="exemplo@exemplo.com"
                  maxLength={150}
                  value={field.value}
                  onChange={(e) => field.onChange(e.target.value)}
                  isRequired
                  hasError={errors?.email?.message}
                  errorMessage={errors?.email?.message}
                  {...field}
                />
              )}
            />
            <Controller
              name="registrationNumber"
              control={control}
              render={({ field }) => (
                <InputText
                  id="registrationNumber"
                  name="registrationNumber"
                  title="CNPJ"
                  placeholder="00.000.000/0000-00"
                  mask="99.999.999/9999-99"
                  hasError={errors?.registrationNumber?.message}
                  errorMessage={errors?.registrationNumber?.message}
                  value={field.value}
                  onChange={(e) => field.onChange(e.target.value)}
                  isRequired
                  {...field}
                />
              )}
            />
            <Controller
              name="contactPeriod"
              control={control}
              render={({ field }) => (
                <Select
                  id="contactPeriod"
                  name="contactPeriod"
                  title="Melhor período para contato"
                  placeholder="Escolha uma opção"
                  options={[
                    { label: "Manhã (10h às 12h)", value: "1" },
                    { label: "Tarde (13 às 17h)", value: "2" },
                    { label: "Noite (18h às 20h)", value: "3" },
                  ]}
                  value={field.value?.value}
                  selected={field.value}
                  onChange={(e) => field.onChange(e.target.value)}
                  hasError={errors?.contactPeriod?.message}
                  errorMessage={errors?.contactPeriod?.message}
                  isRequired
                  {...field}
                />
              )}
            />
            <Controller
              name="phone"
              control={control}
              render={({ field }) => (
                <InputText
                  id="phone"
                  name="phone"
                  title="Telefone"
                  placeholder="(00) 0 0000-0000"
                  mask="(99) 9 9999-9999"
                  hasError={errors?.phone?.message}
                  errorMessage={errors?.phone?.message}
                  value={field.value}
                  onChange={(e) => field.onChange(e.target.value)}
                  isRequired
                  {...field}
                />
              )}
            />
          </S.ContactSectionFormData>

          <S.ContactSectionFormFooter>
            <Button
              type="button"
              variant="outlined"
              onClick={() => {
                if (onToggle) {
                  onToggle(false);
                  reset();
                  window.scrollBy(0, isMobile ? -900 : -500);
                }
              }}
              disabled={isDisabledFields}
            >
              Voltar
            </Button>

            <Button
              type="submit"
              variant="secondary"
              disabled={isDisabledFields}
            >
              Enviar
            </Button>
          </S.ContactSectionFormFooter>
        </S.ContactSectionFormContent>
      </S.ContactSectionFormContainer>

      <ModalResponsive show={isOpenModal}>
        <header className="header">
          <h1>Informações enviadas com sucesso!</h1>
          <p>
            Entraremos em contato para elaborar sua proposta em até 3 dias
            úteis. Até breve!
          </p>
        </header>

        <div className="divider" />

        <div className="footer">
          <Button
            type="button"
            variant={isMobile ? "text" : "outlined"}
            onClick={() => setIsOpenModal(false)}
          >
            Fechar
          </Button>
        </div>
      </ModalResponsive>
    </>
  );
};

ContactSectionForm.defaultProps = {
  isOpen: PropTypes.bool,
  onToggle: PropTypes.func,
};

ContactSectionForm.propTypes = {
  isOpen: false,
  onToggle: null,
};

export default ContactSectionForm;
